// The header bell: recorded 4-hour warnings and breaches on the rows now on the board
// (notifications.ts). Opening the panel marks the items shown as seen in this browser.

import { useEffect, useRef, useState } from 'react';
import { clockTime } from '../board-state.ts';
import { BellIcon } from '../icons.tsx';
import { loadSeen, saveSeen, unreadCount, type AlertNotification } from '../notifications.ts';
import { href } from '../router.ts';
import { AcknowledgeButton } from './AcknowledgeButton.tsx';

export function NotificationsBell({
  items,
  siteId,
  canAcknowledge = false,
}: {
  items: readonly AlertNotification[];
  siteId: string;
  /** The user's role may acknowledge alerts (L4.2); the API checks again. */
  canAcknowledge?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState<ReadonlySet<string>>(loadSeen);
  const box = useRef<HTMLDivElement>(null);

  // Everything shown while the panel is open counts as seen.
  useEffect(() => {
    if (!open) return;
    const ids = items.map((i) => i.id);
    if (ids.every((id) => seen.has(id))) return;
    saveSeen(ids);
    setSeen(new Set(ids));
  }, [open, items, seen]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      if (box.current !== null && !box.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const unread = unreadCount(items, seen);
  const label =
    unread === 0
      ? 'Notifications'
      : `Notifications, ${unread} new recorded 4-hour alert${unread === 1 ? '' : 's'}`;
  return (
    <div className="notifications" ref={box}>
      <button
        type="button"
        className="theme-button notifications-button"
        aria-label={label}
        title={label}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <BellIcon />
        {unread > 0 && (
          <span className="notifications-count" data-testid="notifications-count">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="notifications-panel" role="dialog" aria-label="Notifications">
          <h2>Notifications</h2>
          {items.length === 0 ? (
            <p className="muted">No 4-hour warnings or breaches recorded.</p>
          ) : (
            <ul>
              {items.map((i) => (
                <li key={i.id}>
                  <a
                    className={`notification notification-${i.level}`}
                    href={href({ page: 'patient', encounterId: i.encounterId })}
                    onClick={() => setOpen(false)}
                  >
                    <span className="notification-title">{i.title}</span>
                    <span>
                      {i.name}
                      {i.place === '' ? '' : ` · ${i.place}`}
                    </span>
                    <span className="muted">{clockTime(i.at)}</span>
                    {i.handling !== undefined && <span className="status-note">{i.handling}</span>}
                  </a>
                  {canAcknowledge && (
                    <AcknowledgeButton
                      siteId={siteId}
                      encounterId={i.encounterId}
                      timer={i.timer}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="note">
            Recorded 4-hour alerts only. No push or sound. Acknowledging stops escalation; the alert
            stays on the board.
          </p>
        </div>
      )}
    </div>
  );
}
